import { useCallback, useMemo } from 'react'

import { useUserPreferencesQuery, useUserPreferencesMutation } from 'hooks/api/useUserPreferences'
import type { SavedTableView, TableViewState } from 'types'

const makeId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `view_${Date.now()}_${Math.floor(Math.random() * 1e6)}`
}

/**
 * Per-user saved custom table views (Jira-like saved filters), backed by the
 * company_user `preferences` field. Exposes the saved views for a given table
 * `viewKey`, the active view, the save-state-on-login toggle, and operations to
 * create / delete / activate views and persist last-used view state.
 */
export const useTableViews = (viewKey: string) => {
  const { preferences, isLoading } = useUserPreferencesQuery()
  const { update, isSaving } = useUserPreferencesMutation()

  const savedViews: SavedTableView[] = useMemo(
    () => preferences.savedViews?.[viewKey] ?? [],
    [preferences.savedViews, viewKey],
  )

  const activeViewId: string | null = preferences.activeViewId?.[viewKey] ?? null
  const saveStateOnLogin = Boolean(preferences.saveStateOnLogin)
  const lastViewState: TableViewState | undefined = preferences.lastViewState?.[viewKey]

  const saveView = useCallback(
    async (name: string, state: TableViewState) => {
      const view: SavedTableView = {
        id: makeId(),
        name: name.trim() || 'Untitled view',
        state,
        createdAt: new Date().toISOString(),
      }
      const nextViews = [...savedViews, view]
      await update({
        savedViews: { ...(preferences.savedViews ?? {}), [viewKey]: nextViews },
        activeViewId: { ...(preferences.activeViewId ?? {}), [viewKey]: view.id },
      })
      return view
    },
    [savedViews, update, preferences.savedViews, preferences.activeViewId, viewKey],
  )

  const deleteView = useCallback(
    async (id: string) => {
      const nextViews = savedViews.filter(v => v.id !== id)
      const nextActive = activeViewId === id ? null : activeViewId
      await update({
        savedViews: { ...(preferences.savedViews ?? {}), [viewKey]: nextViews },
        activeViewId: { ...(preferences.activeViewId ?? {}), [viewKey]: nextActive },
      })
    },
    [savedViews, activeViewId, update, preferences.savedViews, preferences.activeViewId, viewKey],
  )

  const setActiveView = useCallback(
    async (id: string | null) => {
      await update({
        activeViewId: { ...(preferences.activeViewId ?? {}), [viewKey]: id },
      })
    },
    [update, preferences.activeViewId, viewKey],
  )

  const persistLastViewState = useCallback(
    async (state: TableViewState) => {
      await update({
        lastViewState: { ...(preferences.lastViewState ?? {}), [viewKey]: state },
      })
    },
    [update, preferences.lastViewState, viewKey],
  )

  return {
    isLoading,
    isSaving,
    savedViews,
    activeViewId,
    saveStateOnLogin,
    lastViewState,
    saveView,
    deleteView,
    setActiveView,
    persistLastViewState,
  }
}
